import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiBarChart2, FiCheck, FiX, FiClock, FiArrowLeft, FiBookOpen } from 'react-icons/fi';
import { results as resultsApi, attempts as attemptsApi } from '../../api';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

export function CandidateResultsPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    resultsApi.getCandidate().then(r => setData(r.data.data || [])).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5 animate-fade-in">
      <h1 className="text-2xl font-black text-gray-900">My Results</h1>
      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 card animate-pulse" />)}</div>
      ) : data.length === 0 ? (
        <div className="card p-12 text-center"><FiBarChart2 size={40} className="text-gray-200 mx-auto mb-3" /><p className="text-gray-400">No results yet</p></div>
      ) : (
        <div className="space-y-3">
          {data.map(r => (
            <Link key={r.id} to={`/candidate/results/${r.attempt_id}`}
              className="card p-5 block hover:shadow-card-hover transition-shadow">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center flex-shrink-0 font-black ${r.is_passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  <span className="text-xl">{parseFloat(r.percentage).toFixed(0)}%</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900">{r.exam_title}</p>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><FiCheck size={10} className="text-emerald-500" /> {r.correct_answers} correct</span>
                    <span className="flex items-center gap-1"><FiX size={10} className="text-red-500" /> {r.wrong_answers} wrong</span>
                    <span className="flex items-center gap-1"><FiClock size={10} /> {Math.round(r.time_taken_seconds / 60)} min</span>
                    <span>{formatDistanceToNow(new Date(r.evaluated_at), { addSuffix: true })}</span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className={r.is_passed ? 'badge-pass' : 'badge-fail'}>{r.is_passed ? 'PASSED' : 'FAILED'}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ResultDetailPage() {
  const { attemptId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnswers, setShowAnswers] = useState(false);

  useEffect(() => {
    attemptsApi.getResult(attemptId).then(r => setResult(r.data.data)).catch(() => toast.error('Failed to load result')).finally(() => setLoading(false));
  }, [attemptId]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-navy/20 border-t-navy rounded-full animate-spin" /></div>;
  if (!result) return <div className="text-center py-20 text-gray-400">Result not found</div>;
  if (result.submitted) return (
    <div className="card p-12 text-center max-w-md mx-auto">
      <FiCheck size={40} className="text-emerald-500 mx-auto mb-4" />
      <h2 className="text-xl font-black mb-2">Exam Submitted!</h2>
      <p className="text-gray-500">{result.message || 'Your results will be announced soon.'}</p>
      <Link to="/candidate/results" className="btn-primary mt-6 inline-flex">← Back to Results</Link>
    </div>
  );

  const pct = parseFloat(result.percentage);
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <div className="flex items-center gap-3">
        <Link to="/candidate/results" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FiArrowLeft size={18} /></Link>
        <div>
          <h1 className="text-xl font-black text-gray-900">{result.exam_title}</h1>
          <p className="text-gray-500 text-sm">Result Analysis</p>
        </div>
      </div>

      {/* Score card */}
      <div className={`card p-8 text-center border-2 ${result.is_passed ? 'border-emerald-200' : 'border-red-200'}`}>
        <div className="flex items-center justify-center gap-8 flex-wrap">
          {/* Circular progress */}
          <div className="relative w-32 h-32">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="none" stroke="#f0f0f0" strokeWidth="8" />
              <circle cx="50" cy="50" r="45" fill="none"
                stroke={result.is_passed ? '#10b981' : '#ef4444'} strokeWidth="8"
                strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
                style={{ transition: 'stroke-dashoffset 1s ease-in-out' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-black text-gray-900">{pct.toFixed(1)}%</span>
            </div>
          </div>
          <div className="text-left">
            <div className={`text-4xl font-black mb-1 ${result.is_passed ? 'text-emerald-600' : 'text-red-600'}`}>
              {result.is_passed ? '✓ PASSED' : '✗ FAILED'}
            </div>
            <p className="text-gray-500">Score: <strong>{parseFloat(result.raw_score).toFixed(1)} / {result.total_marks}</strong></p>
            <p className="text-gray-500">Required: <strong>{result.passing_marks}%</strong></p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mt-8 pt-6 border-t border-gray-100">
          {[
            { label: 'Correct', value: result.correct_answers, color: 'text-emerald-600' },
            { label: 'Wrong', value: result.wrong_answers, color: 'text-red-600' },
            { label: 'Skipped', value: result.skipped_questions, color: 'text-gray-500' },
            { label: 'Time', value: `${Math.round(result.time_taken_seconds / 60)}m`, color: 'text-navy' },
          ].map(({ label, value, color }) => (
            <div key={label}>
              <div className={`text-2xl font-black ${color}`}>{value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Answer review */}
      {result.answers && (
        <div className="card p-5">
          <button onClick={() => setShowAnswers(s => !s)}
            className="w-full flex items-center justify-between font-bold text-gray-900">
            <span className="flex items-center gap-2"><FiBookOpen size={18} /> Answer Review</span>
            <span className="text-navy text-sm font-semibold">{showAnswers ? 'Hide' : 'Show'}</span>
          </button>
          {showAnswers && (
            <div className="mt-4 space-y-4">
              {result.answers.map((a, i) => {
                const isCorrect = a.is_answered && a.selected_answer?.toLowerCase() === a.correct_answer?.toLowerCase();
                return (
                  <div key={i} className={`p-4 rounded-xl border-2 ${isCorrect ? 'border-emerald-200 bg-emerald-50/50' : a.is_answered ? 'border-red-200 bg-red-50/50' : 'border-gray-200'}`}>
                    <p className="font-medium text-gray-900 text-sm mb-3">{i + 1}. {a.question_text}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {['a','b','c','d'].filter(o => a[`option_${o}`]).map(o => (
                        <div key={o} className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-2 ${
                          a.correct_answer?.includes(o) ? 'bg-emerald-100 text-emerald-800 font-semibold' :
                          a.selected_answer?.includes(o) && !a.correct_answer?.includes(o) ? 'bg-red-100 text-red-800' :
                          'bg-white text-gray-600'}`}>
                          <span className="font-bold uppercase">{o}.</span>{a[`option_${o}`]}
                          {a.correct_answer?.includes(o) && <FiCheck size={10} className="ml-auto" />}
                        </div>
                      ))}
                    </div>
                    {!a.is_answered && <p className="text-xs text-gray-400 mt-2">Not answered</p>}
                    {a.explanation && <p className="text-xs text-gray-500 mt-2 italic">💡 {a.explanation}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CandidateResultsPage;
